import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useEffect, useRef} from "react";
import {useSigma} from "@react-sigma/core";
import {useFileRegions} from "../../hooks/useJsonLoader";
import {clearCanvas, drawRegions, resizeListener} from "../../utils/draw";

const GlobalRegionsController = () => {
  const {selectedGenes} = useTheCellMapContext();

  const sigma = useSigma();

  const regions = useFileRegions();

  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Create a new canvas for regions and insert it after the edgeLabels canvas
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    const target = sigma.getCanvases().nodes;

    if (!canvas || !canvas.parentElement || !target || !target.parentElement) return;

    canvas.style.position = target.style.position;
    target.parentElement.insertBefore(canvas, target);

    const handleResize = () => resizeListener(sigma, canvas);

    sigma.on("resize", handleResize);

    // Initialize size & DPR transform immediately (and once more after layout settles)
    requestAnimationFrame(handleResize);

    return () => {
      sigma.off("resize", handleResize);
    };
  }, [sigma]);

  /**
   * Draw regions if we have selected genes and regions are loaded
   */
  useEffect(() => {
    if (selectedGenes.length === 0) return; // No selected genes, no need to draw regions

    const beforeRender = () => {
      clearCanvas(canvasRef.current);
    };

    const afterRender = () => {
      // Draw regions
      drawRegions(
        sigma,
        canvasRef.current?.getContext("2d") || null,
        !!regions ? regions : null, // FIXME: handle undefined
        false,
      );
    };

    sigma.on("beforeRender", beforeRender);
    sigma.on("afterRender", afterRender);

    requestAnimationFrame(beforeRender);  // Force a render to draw regions immediately
    requestAnimationFrame(afterRender);

    return () => {
      sigma.off("beforeRender", beforeRender);
      sigma.off("afterRender", afterRender);
      clearCanvas(canvasRef.current);
    };
  }, [sigma, regions, selectedGenes, canvasRef.current]);

  return <canvas ref={canvasRef} className="sigma-regions"></canvas>;
};

export default GlobalRegionsController;
